import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Mail,
  Phone,
  Globe2,
  Shield,
  Languages,
  HeartPulse,
  ArrowRight,
  Clock,
  GraduationCap,
  FileCheck2,
  Building2,
} from "lucide-react";

// Tailwind-only, polished landing page
// - Desktop: sticky enquiry form on the right
// - Mobile: bottom sticky CTA + (optional) slide-over form
// - Motion touches via framer-motion
export default function NursingGermanyLanding() {
  const navigate = useNavigate();
  const [mobileFormOpen, setMobileFormOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    // TODO: hook to Google Sheets / API endpoint
    alert("Thanks! Our counsellor will contact you shortly.");
    setForm({ name: "", phone: "", email: "", message: "" });
  };

  const Feature = ({ icon: Icon, title, desc }) => (
    <div className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mt-1 grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
        <Icon size={20} />
      </div>
      <div>
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-1 text-sm text-slate-600">{desc}</p>
      </div>
    </div>
  );

  const Bullet = ({ children }) => (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
      <span className="text-[15px] leading-6 text-slate-700">{children}</span>
    </li>
  );

  const Section = ({ id, icon: Icon, title, children }) => (
    <section id={id} className="mx-auto max-w-7xl px-4 py-10">
      <div className="mb-4 flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
          <Icon size={20} />
        </div>
        <h2 className="text-2xl font-bold">{title}</h2>
      </div>
      {children}
    </section>
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-50 to-white text-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-emerald-600/10 ring-1 ring-emerald-600/30 grid place-items-center">
              <HeartPulse className="h-5 w-5 text-emerald-700" />
            </div>
            <div className="leading-tight">
              <div className="font-semibold">Nursing Jobs in Germany</div>
            </div>
          </div>

          <div className="hidden items-center gap-5 md:flex">
            <a
              href="mailto:info@unithink.in"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-emerald-700"
            >
              <Mail size={16} /> info@unithink.in
            </a>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700"
            >
              Enquire Now <ArrowRight size={16} />
            </button>
          </div>

          {/* Mobile open form */}
          <button
            onClick={() => setMobileFormOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm hover:bg-slate-50 md:hidden"
          >
            Enquire <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_400px_at_10%_0%,rgba(16,185,129,0.08),transparent),radial-gradient(800px_400px_at_90%_0%,rgba(59,130,246,0.07),transparent)]" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 pb-10 pt-10 md:grid-cols-12 md:pb-16 md:pt-14">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-7 lg:col-span-8"
          >
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
              Build your nursing career in Germany
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-slate-600">
              High-demand roles, excellent pay, and clear pathways to permanent residency. Start as a
              Nursing Assistant or arrive job‑ready as a Registered Nurse with B2 German.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                Salary €2,800–€4,500 + bonuses
              </span>
              <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                PR pathway ≈ 5 years
              </span>
              <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">
                200k+ nurses needed
              </span>
            </div>

            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              <Bullet>Work visa typically with B1; full registration with B2 (Goethe/TELC/ÖSD)</Bullet>
              <Bullet>State recognition (Anerkennung) & 6–9 month adaptation program</Bullet>
              <Bullet>Roles across hospitals, elderly care, dialysis, ER, OR, pediatrics & more</Bullet>
              <Bullet>Benefits: public insurance, pension, childcare, 38–40 hr weeks</Bullet>
            </ul>

            <div className="mt-7 flex gap-3">
              <button
                onClick={() => navigate("/contact")}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700"
              >
                Enquire Now <ArrowRight size={16} />
              </button>
              <a
                href="mailto:info@unithink.in"
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-slate-800 shadow-sm hover:bg-slate-50"
              >
                <Mail size={18} /> Email Us
              </a>
            </div>
          </motion.div>

          {/* Desktop Form (sticky right) */}
          {/*<aside className="md:col-span-5 lg:col-span-4">
            <div id="enquire" className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Enquire Now</h3>
                <Shield className="h-5 w-5 text-emerald-600" />
              </div>
              <form onSubmit={onSubmit} className="space-y-3">
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Full Name"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-emerald-600 focus:outline-none"
                  required
                />
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="Phone Number"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-emerald-600 focus:outline-none"
                  required
                />
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="Email Address"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-emerald-600 focus:outline-none"
                  required
                />
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Qualification / German level (optional)"
                  rows={3}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-emerald-600 focus:outline-none"
                />
                <button
                  type="submit"
                  className="w-full rounded-xl bg-emerald-600 px-4 py-2 font-medium text-white shadow hover:bg-emerald-700"
                >
                  Submit
                </button>
                <p className="text-center text-xs text-slate-500">
                  or email us at <a className="underline" href="mailto:info@unithink.in">info@unithink.in</a>
                </p>
              </form>
            </div>
          </aside>*/}
          {/* Desktop Form (sticky right) */}
{/* Desktop Button (sticky right) */}
<aside className="md:col-span-8 lg:col-span-4 mt-20 ml-7 flex justify-center items-start pt-6">
  <button
    onClick={() =>
      window.open("https://forms.gle/rBnMLUWVwj8EToiE8", "_blank")
    }
    className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 text-sm font-medium text-white shadow hover:bg-emerald-700"
  >
    Fill the form now <ArrowRight size={16} />
  </button>
</aside>


        </div>
      </section>

      {/* Value Props */}
      <section className="mx-auto max-w-7xl px-4 py-10">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Feature
            icon={Languages}
            title="German Language"
            desc="B1 for visa in most routes; B2 required to register as a fully recognized nurse. Many employers co‑fund B2."
          />
          <Feature
            icon={Clock}
            title="Adaptation 6–9 months"
            desc="Finish Kenntnisprüfung or Anpassungslehrgang after arrival, then upgrade salary & status."
          />
          <Feature
            icon={Building2}
            title="Hospitals & Care Homes"
            desc="Opportunities across ICU, ER, OR, dialysis, pediatrics, geriatrics and more."
          />
        </div>
      </section>

      {/* Process Timeline */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <h2 className="text-2xl font-bold">Step‑by‑step Process</h2>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-5">
            <Step icon={FileCheck2} title="1. Document check" desc="Evaluation + sworn translations (degree, registration, clinical logs)." />
            <Step icon={Shield} title="2. Anerkennung" desc="State authority compares training & lists gaps if any." />
            <Step icon={Globe2} title="3. Visa + Offer" desc="Submit recognition decision, B1/B2 proof, and job offer." />
            <Step icon={GraduationCap} title="4. Adaptation" desc="6–9 months → Kenntnisprüfung / Anpassungslehrgang." />
            <Step icon={ArrowRight} title="5. Full License" desc="Salary upgrade; Blue Card eligible after recognition." />
          </div>
        </div>
      </section>

      {/* Rich Content Sections from your brief */}
      <Section id="overview" icon={HeartPulse} title="Nursing jobs in Germany">
        <p className="max-w-4xl text-slate-700">
          Nursing roles are highly respected in Germany, and demand is massive. If your degree is recognized and you
          have the right to reside, hospitals and care institutions actively hire. Success depends on German language
          level (B2 minimum for full license), country of origin, and where you completed your degree.
        </p>
      </Section>

      <Section id="types" icon={Building2} title="Types of Nursing Jobs in Germany">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {[
            "Staff nurse",
            "Office nurse",
            "Hospital nurse",
            "Geriatric nurse",
            "Pediatric nurse",
            "Gynecology nurse",
            "Dialysis nurse",
            "ER nurse",
            "OR nurse",
            "Senior nurse",
            "Nursing Assistant",
            "Senior caregiver",
            "Caregiver assistant",
          ].map((t) => (
            <span key={t} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              {t}
            </span>
          ))}
        </div>
      </Section>

      <Section id="docs" icon={FileCheck2} title="Documents Required (notarized)">
        <ul className="grid list-disc gap-1 pl-5 text-slate-700 sm:grid-cols-2">
          {[
            "CV / Resume",
            "Health Certificate",
            "Passport photos",
            "Experience certificates (if any)",
            "RN Registration certificate",
            "10th & 12th certificates",
            "Higher Secondary or equivalent",
            "Professional qualification certificate",
            "Curriculum / Syllabus",
            "Consolidated mark sheets",
            "Semester‑wise mark sheets (if available)",
            "Provisional certificate (if available)",
            "Passport copy (front & back)",
          ].map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      </Section>

      <Section id="indian-route" icon={Globe2} title="Nursing jobs in Germany for Indian nurses">
        <ul className="space-y-2 text-slate-700">
          <li>• German Language Student route → limited work initially if you don’t know German.</li>
          <li>
            • Work‑permit route with B1 → join as Nursing Assistant via direct employer / recruiters and upgrade later.
          </li>
          <li>
            • Preferred route: achieve B2 in India → arrive job‑ready as Registered Nurse (state Board decides final eligibility).
          </li>
        </ul>
      </Section>

      <Section id="freshers" icon={GraduationCap} title="Nursing Jobs in Germany for Freshers">
        <p className="max-w-4xl text-slate-700">
          Freshers can apply if they hold a recognized degree/diploma and meet language requirements. Some internships
          or volunteering can strengthen your profile. Note: nursing is regulated; ensure registration and visa criteria
          are met per state.
        </p>
      </Section>

      <Section id="lang" icon={Languages} title="German Language Requirements for Indian Nurses">
        <ul className="space-y-2 text-slate-700">
          <li>• B1 is commonly needed for visa processing.</li>
          <li>• B2 is mandatory for final registration (GOETHE/TELC/ÖSD).</li>
          <li>• Many employers fund B2 training and exam costs.</li>
          <li>• Better German → faster license, career growth, and patient trust.</li>
        </ul>
      </Section>

      <Section id="earnings" icon={Clock} title="Your Earning Potential in Germany">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-emerald-200 bg-white p-6 text-center">
            <div className="text-sm text-slate-600">Adaptation Phase</div>
            <div className="mt-1 text-2xl font-bold">€2,800–€3,500</div>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-white p-6 text-center">
            <div className="text-sm text-slate-600">After Full Recognition</div>
            <div className="mt-1 text-2xl font-bold">€3,800–€4,500</div>
            <div className="text-xs text-slate-500">+ Night/Weekend allowances</div>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-white p-6 text-center">
            <div className="text-sm text-slate-600">Annual Potential</div>
            <div className="mt-1 text-2xl font-bold">€55,000+*</div>
            <div className="text-xs text-slate-500">*with bonuses & shifts</div>
          </div>
        </div>
      </Section>

      <Section id="benefits" icon={Shield} title="Benefits of Working in Germany">
        <ul className="grid gap-2 text-slate-700 sm:grid-cols-2">
          <li>• Permanent residency pathways</li>
          <li>• Free / subsidized childcare</li>
          <li>• 38–40 hour workweek under labour laws</li>
          <li>• Public health insurance from day one</li>
          <li>• Employer‑funded pension (portable after 5 years)</li>
          <li>• Multicultural teams & modern equipment</li>
          <li>• Weiterbildung (specialized training) options</li>
        </ul>
      </Section>

      <Section id="become-rn" icon={GraduationCap} title="How do you become a registered nurse in Germany?">
        <ol className="list-decimal space-y-2 pl-5 text-slate-700">
          <li>Learn German to a professional level (B2 for full registration).</li>
          <li>Get equivalency/recognition for non‑EU degrees from state authorities.</li>
          <li>Apply for the relevant visa and secure a job offer.</li>
          <li>Finish a 3‑year programme (for trainees) or adaptation + state exam depending on profile.</li>
          <li>Register with the Nursing Board in your chosen state.</li>
        </ol>
      </Section>

      <Section id="demand" icon={HeartPulse} title="Rising Demand for Nursing Jobs in Germany!">
        <p className="max-w-4xl text-slate-700">
          An ageing population and rising life expectancy have created a persistent shortage. Hospitals and nursing homes
          actively recruit internationally to close the gap, opening strong opportunities for Indian nurses.
        </p>
      </Section>

      <Section id="eligibility-criteria" icon={Shield} title="Work as a Registered Nurse: Eligibility Criteria">
        <ul className="space-y-2 text-slate-700">
          <li>• BSc/GNM/MSc in Nursing</li>
          <li>• B2 German (GOETHE/TELC/ÖSD)</li>
        </ul>
      </Section>

      <Section id="duties" icon={CheckCircle2} title="Nursing Duties">
        <ul className="space-y-2 text-slate-700">
          <li>• Patient care & independent observation</li>
          <li>• Counselling & assistance for in/out‑patients</li>
          <li>• Execute medical directives and support procedures</li>
          <li>• Family education & communication</li>
        </ul>
      </Section>

      <Section id="perks" icon={Shield} title="Perks of Nursing Jobs in Germany for Indian Nurses">
        <ul className="space-y-2 text-slate-700">
          <li>• High demand and growth across health sectors</li>
          <li>• Strong job security via works councils</li>
          <li>• Family reunification options; spouse can work</li>
          <li>• PR eligibility after 5+ years (subject to rules)</li>
          <li>• Unlimited job contracts are common</li>
        </ul>
      </Section>

      <Section id="new-lyf" icon={Phone} title="How Can Unithink assist you?">
        <ul className="space-y-2 text-slate-700">
          <li>• Online/Offline German classes</li>
          <li>• Registration guidance & document translations</li>
          <li>• Employer interviews & job contracts</li>
          <li>• Post‑landing support in Germany</li>
          <li>• Family relocation assistance</li>
        </ul>
      </Section>

      <Section id="conclusion" icon={HeartPulse} title="Conclusion">
        <p className="max-w-4xl text-slate-700">
          Germany offers Indian nurses clear, structured pathways to high‑paying roles, legal residency, and global
          careers. With language support and recognition processes in place, it’s an excellent move for long‑term
          growth abroad.
        </p>
      </Section>

      {/* FAQ */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 py-12">
          <h2 className="text-2xl font-bold">FAQs</h2>
          <div className="mt-6 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            <Faq q="Are there any jobs for a nursing diploma in Germany?" a="Yes, subject to state recognition and language level. Many roles start as Nursing Assistant while you complete recognition." />
            <Faq q="How can I work as a nurse assistant in Germany?" a="Secure B1 German and an employer offer; arrive on a work permit and complete adaptation/recognition for RN." />
            <Faq q="What are the job opportunities for Indian nurses in Germany?" a="Hospitals, elderly care, dialysis, ER/OR, pediatrics, and more across states." />
            <Faq q="Is it possible to get a nursing job with 1 year of experience and B2?" a="Yes. With B2 and recognition, you can target RN roles; otherwise start as Assistant and upgrade." />
            <Faq q="Is B1 enough to start?" a="B1 can be sufficient for visa in some routes; B2 is required for full registration as RN." />
            <Faq q="Which German level is required?" a="B1 for visa (typical), B2 for Anerkennung/registration (Goethe/TELC/ÖSD)." />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 py-12">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-emerald-50 to-white p-6 md:p-10">
          <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <h3 className="text-xl font-semibold">Check your eligibility in 60 seconds</h3>
              <p className="mt-2 text-slate-700">
                Share your qualification and German level. We’ll guide you on the right route: language student,
                Nursing Assistant, or Registered Nurse.
              </p>
            </div>
            <div className="text-right">
              <button
  onClick={() =>
    window.open("https://forms.gle/rBnMLUWVwj8EToiE8", "_blank")
  }
  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700"
>
  Fill the form now <ArrowRight size={16} />
</button>

            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 sm:flex-row">
          <p className="text-sm text-slate-600">©️ {new Date().getFullYear()} UniThink • Nursing Jobs in Germany</p>
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <a className="inline-flex items-center gap-1 hover:text-emerald-700" href="mailto:info@unithink.in">
              <Mail size={16} /> info@unithink.in
            </a>
            
          </div>
        </div>
      </footer>

      {/* Mobile bottom CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 block border-t border-slate-200 bg-white/95 p-3 backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
          <span className="text-sm font-medium">Ready to apply?</span>
          <button
            onClick={() => setMobileFormOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow"
          >
            Enquire <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Mobile slide-over form (optional) */}
      {/**
      {mobileFormOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileFormOpen(false)} />
          <div className="absolute inset-y-0 right-0 w-full max-w-md rounded-l-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Enquire Now</h3>
              <button onClick={() => setMobileFormOpen(false)} className="rounded-lg border border-slate-200 px-3 py-1 text-sm">Close</button>
            </div>
            <form onSubmit={onSubmit} className="space-y-3">
              <input name="name" value={form.name} onChange={onChange} placeholder="Full Name" className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-emerald-600 focus:outline-none" required />
              <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone Number" className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-emerald-600 focus:outline-none" required />
              <input type="email" name="email" value={form.email} onChange={onChange} placeholder="Email Address" className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-emerald-600 focus:outline-none" required />
              <textarea name="message" value={form.message} onChange={onChange} placeholder="Qualification / German level (optional)" rows={3} className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-emerald-600 focus:outline-none" />
              <button type="submit" className="w-full rounded-xl bg-emerald-600 px-4 py-2 font-medium text-white shadow hover:bg-emerald-700">Submit</button>
              <p className="text-center text-xs text-slate-500">or email us at {" "}
                <a className="underline" href="mailto:info@new-lyf.com">info@new-lyf.com</a>
              </p>
            </form>
          </div>
        </div>
      )}
      **/}
    </div>
  );
}

function Step({ icon: Icon, title, desc }) {
  return (
    <div className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition">
      <div className="mb-3 inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-1 text-sm text-slate-700">
        <Icon size={16} className="text-emerald-700" />
        <span className="font-medium">{title}</span>
      </div>
      <p className="text-sm text-slate-700">{desc}</p>
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <details open={open} onToggle={(e) => setOpen(e.target.open)} className="group">
      <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4">
        <span className="font-medium text-slate-800">{q}</span>
        <ArrowRight className={`h-4 w-4 transition-transform ${open ? "rotate-90 text-emerald-700" : "text-slate-400"}`} />
      </summary>
      <div className="px-5 pb-5 text-sm text-slate-700">{a}</div>
    </details>
  );
}